"use strict";
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.RichText = void 0;
const jsx_runtime_1 = require("@emotion/react/jsx-runtime");
const app_1 = require("@obvio/app");
const ui_1 = require("@obvio/ui");
const react_1 = require("react");
function RichText({ value, asMap, tagMap, render = 'div', loading = false, }) {
    const parsed = (0, react_1.useMemo)(() => (value ? parse(value, asMap, tagMap) : null), [value, asMap, tagMap]);
    if (loading) {
        return (0, jsx_runtime_1.jsx)(ui_1.Skeleton, { rows: 5 });
    }
    return ((0, jsx_runtime_1.jsx)(app_1.Context, Object.assign({ name: "richText", render: render }, { children: parsed })));
}
exports.RichText = RichText;
function NewLine() {
    return (0, jsx_runtime_1.jsx)("br", {});
}
function CodeBlock(_a) {
    var { children } = _a, props = __rest(_a, ["children"]);
    return ((0, jsx_runtime_1.jsx)("pre", Object.assign({}, props, { children: (0, jsx_runtime_1.jsx)("code", { children: children }) })));
}
const InlineCode = app_1.styled.code `
  display: inline-block;
`;
const FontHeading = (0, app_1.styled)(app_1.Text) `
  ${(theme) => { var _a; return [(_a = theme.typography.h2) === null || _a === void 0 ? void 0 : _a.css]; }}
  font-family: ${(theme, { $font }) => $font === 'secondary' ? theme.secondaryFont : theme.font};
  display: inline;
`;
function textRenderer(rendererTag, { content, options, }) {
    var _a, _b, _c;
    const tag = (_b = (_a = options === null || options === void 0 ? void 0 : options.tagMap) === null || _a === void 0 ? void 0 : _a[rendererTag]) !== null && _b !== void 0 ? _b : rendererTag;
    const as = (_c = options === null || options === void 0 ? void 0 : options.asMap) === null || _c === void 0 ? void 0 : _c[rendererTag];
    return ((0, jsx_runtime_1.jsx)(app_1.Text, Object.assign({ tag: tag, as: as }, { children: content })));
}
const LIST_BLOCK = {
    [app_1.RichTextListType.UL]: (content) => (0, jsx_runtime_1.jsx)("ul", { children: content }),
    [app_1.RichTextListType.OL]: (content) => (0, jsx_runtime_1.jsx)("ol", { children: content }),
};
const BLOCK = {
    [app_1.RichTextBlockType.H2]: (props) => textRenderer('h2', props),
    [app_1.RichTextBlockType.H3]: (props) => textRenderer('h3', props),
    [app_1.RichTextBlockType.QUOTE]: (props) => textRenderer('blockquote', props),
    [app_1.RichTextBlockType.CODEBLOCK]: ({ content }) => ((0, jsx_runtime_1.jsx)(CodeBlock, { children: content })),
    [app_1.RichTextBlockType.P]: (props) => textRenderer('p', props),
};
const INLINE_RENDERERS = {
    BOLD: ({ content }) => (0, jsx_runtime_1.jsx)("b", { children: content }),
    CODE: ({ content }) => (0, jsx_runtime_1.jsx)(InlineCode, { children: content }),
    UNDERLINE: ({ content }) => (0, jsx_runtime_1.jsx)("u", { children: content }),
    ITALIC: ({ content }) => (0, jsx_runtime_1.jsx)("i", { children: content }),
    'FONT-1': ({ content, options }) => {
        var _a, _b;
        return ((0, jsx_runtime_1.jsx)(FontHeading, Object.assign({ tag: "h2", as: (_b = (_a = options.asMap) === null || _a === void 0 ? void 0 : _a.h2) !== null && _b !== void 0 ? _b : 'h2' }, options, { children: content })));
    },
    'FONT-2': ({ content, options }) => {
        var _a, _b;
        return ((0, jsx_runtime_1.jsx)(FontHeading, Object.assign({ tag: "h2", as: (_b = (_a = options.asMap) === null || _a === void 0 ? void 0 : _a.h2) !== null && _b !== void 0 ? _b : 'h2', "$font": "secondary" }, options, { children: content })));
    },
};
function richTextSpan(node, options) {
    var _a;
    return (_a = node.styles) === null || _a === void 0 ? void 0 : _a.reduce((acc, style) => { var _a, _b; return (_b = (_a = INLINE_RENDERERS[style]) === null || _a === void 0 ? void 0 : _a.call(INLINE_RENDERERS, { content: acc, options })) !== null && _b !== void 0 ? _b : acc; }, node.text);
}
function richTextNodeRenderer(node, options) {
    return ((0, app_1.isRichTextLinkNode)(node) ? ((0, jsx_runtime_1.jsx)(app_1.Link, Object.assign({ href: node.url }, { children: node.children.map((element, idx) => ((0, jsx_runtime_1.jsx)(react_1.Fragment, { children: richTextNodeRenderer(element, options) }, idx))) }))) : (richTextSpan(node, options)));
}
function richTextListRenderer(list, options) {
    const listContent = list.children.map((listItem, idx) => {
        const content = listItem.children.map((node, idxInner) => ((0, jsx_runtime_1.jsx)(react_1.Fragment, { children: (0, app_1.isRichTextListNode)(node)
                ? richTextListRenderer(node, options)
                : richTextNodeRenderer(node, options) }, idxInner)));
        return ((0, jsx_runtime_1.jsx)(app_1.Text, Object.assign({ tag: "li" }, { children: content }), idx));
    });
    return LIST_BLOCK[list.type](listContent);
}
function parse(text, asMap, tagMap) {
    if (!(text === null || text === void 0 ? void 0 : text.trim())) {
        return null;
    }
    const structure = (0, app_1.getRichTextStructure)(text);
    return structure.map((block, idx) => {
        const content = (0, app_1.isRichTextListNode)(block) ? (richTextListRenderer(block, { asMap, tagMap })) : (0, app_1.isRichTextNewLineNode)(block) ? ((0, jsx_runtime_1.jsx)(NewLine, {})) : (BLOCK[block.type]({
            content: block.children.map((element, elIdx) => ((0, jsx_runtime_1.jsx)(react_1.Fragment, { children: richTextNodeRenderer(element, { asMap, tagMap }) }, elIdx))),
            options: { asMap, tagMap },
        }));
        return (0, jsx_runtime_1.jsx)(react_1.Fragment, { children: content }, idx);
    });
}
